<script setup>
// 거래소개 — 반응형으로 옮긴 두 번째 섹션.
//
// 원본: Figma 홈(ver1) 4133:20687 (1920x1388)
//   콘텐츠 x 200~1706 (1506), y 200~1188 (988) — 위아래 여백 200
//   왼쪽 이미지 820x988 (둥근모서리 40)
//   간격 100 (이미지 오른끝 1020 → 글 왼끝 1120)
//   오른쪽 글 칼럼 585
//     눈썹    y 200  테두리 #a66e08, 15px semibold
//     제목    y 262 / 328  56px bold 행간 66
//     소제목  y 444  22px semibold 행간 30
//     본문    y 484  20px 투명도 50% 행간 28, 문단 4개(사이 28)
//     버튼    y 944  16px medium, 높이 42
//     구분선  y 1036 #d9d9d9 1px
//     인증    y 1087 제목 18px + 배지 60x60
//
// 원본에서 바꾼 것
//   1) 절대좌표를 걷어내고 두 칸 격자에 담았다. 칸 비율은 원본 그대로 820 : 585 다.
//      1024 아래에서는 한 칸으로 접힌다(이미지 위, 글 아래).
//   2) 칸 비율을 546 : 859 로 바꿨다(2026-09-26 요청). 원본은 820 : 585 였다.
//      이미지를 원본의 2/3 으로 줄이고 그만큼을 글에 줬다 — 글 칸이 1.47 배가 되면서
//      본문의 3 줄짜리 문단이 2 줄로 줄어든다.
//      이미지는 칸 높이를 그대로 채운다(h-full). 두 칸이 격자 한 줄이라 글 칸과
//      높이가 저절로 같아진다.
//      한때 이미지를 sticky 로 붙여 글만 지나가게 했는데 걷었다(2026-09-26 요청).
//   3) 제목만 clamp 로 감쌌다. 1280 이상에서는 원본 56px 에 걸려 더 커지지 않는다.
//      나머지 글자는 전부 원본 값 그대로 고정이다.
//   4) 구분선은 원본이 603 인데 칼럼(585)보다 18 넓다. 칼럼 폭에 맞췄다.
//
// 배경은 제 것을 갖지 않는다(bare) — /home2 는 섹션 뒤에 깔린 디졸브 배경이 색을 맡는다.
import { computed, ref } from 'vue'
import { useDesignScale } from '@/composables/useDesignScale.js'
import {
  SCENE_REVEAL_DELAY_MS,
  SCENE_TEXT_STAGGER_MS,
  TRANSITION_MS,
  sceneTextReveal,
} from '@/data/homeMotionTiming.js'
import imgRectangle240649917 from '@/assets/figma/829a165987a296c6af98670390d79535e939d093.png'
import imgAsi1 from '@/assets/figma/17120927140354d8ddcb33c6b6122a57228813aa.png'
import imgImage12 from '@/assets/figma/0852bf42617039e8bd9220ea960f1992b675158f.png'
import imgIso1 from '@/assets/figma/f9954d663361961f69b548ddb256b4fb2cfb7dd8.png'
import imgHallmarkResearchInstitute1 from '@/assets/figma/077b5a117c45071b9e779dac9b25f74be0c3b224.png'
import img1 from '@/assets/figma/2d1be5512ae1a5a59cf85b8e4916a832650e6b47.png'
import imgArrowUpRight from '@/assets/figma/ce758decb7c2faf9d9463ba477e511651d0b1f29.svg'

const props = defineProps({
  // 현재 스토리보드 프레임 번호 (홈1-N)
  step: { type: Number, default: 30 },
  // 배경을 끄고 나온다.
  bare: { type: Boolean, default: false },
})

// 세로 리듬 배율. 여백을 창 폭에 맞춰 줄인다 — 글자는 건드리지 않는다.
// 자세한 이유는 composables/useDesignScale.js 에 있다.
const root = ref(null)
const { scale: rhythm } = useDesignScale(root)

// 스크롤 포인트가 둘이다(2026-10-01). 다른 섹션은 한 번에 다 올라오는데
// 여기만 오른쪽 글이 길어(776) 두 번에 나눈다.
//   30  뱃지 + 제목
//   31  본문 + 버튼 + 구분선 + 인증
// 시점은 HomeSceneView 가 정한다(SECTIONS 의 frame / frame2).
const FIRST_FRAME = 30
const SECOND_FRAME = 31
const headOn = computed(() => props.step >= FIRST_FRAME)
const bodyOn = computed(() => props.step >= SECOND_FRAME)

// 아래에서 위로 드러나는 역커튼. 원본 연출 그대로다.
// order 0 = 머리말(뱃지·제목), 1 = 본문 아래 전부.
// 둘이 각자 제 스크롤 포인트를 가지므로 터울(stagger)은 주지 않는다 - 0 으로 넘긴다.
const curtain = (order) =>
  sceneTextReveal(order === 0 ? headOn.value : bodyOn.value, 0)

const BODY = [
  'Real Money is Real Gold의 브랜드 슬로건과 철학을 중심으로 전 세계에서 가장 오래된 실물자산이자 그 가치는 글로벌 기준의 가치평가로 원화가치로 변동이 되지 않습니다.',
  '대한민국에서 유일한 글로벌 품질 인증과 함량에 대한 보증을 통하여 가장 안전하게 가장 저렴한 가격 비교를 통해 실물자산의 거래를 보장합니다.',
  'Real Gold Market은 오프라인 스토어와 온라인을 통합했으며, 금. 은 실물자산의 주요 모든 브랜드의 거래와 리사이클(중매거래) 거래를 연결하였습니다.',
  '대한민국 중매거래 1위 거래소에서 대한민국 실물거래 1위의 금. 은 거래소로 여러분의 자산투자 포트폴리오와 자산관리로 실물자산 운용의 생태계를 만들어갑니다.',
]

// 인증 배지. 각 칸의 제목과 그 아래 배지들.
const CERTS = [
  {
    label: '글로벌 인증 기준',
    badges: [
      { src: imgAsi1, cls: 'size-[36px]' },
      { src: imgImage12, cls: 'h-[21px] w-[44px]' },
    ],
  },
  {
    label: '국내 품질 인증',
    badges: [
      { src: imgIso1, cls: 'h-[31px] w-[38px]' },
      { src: imgHallmarkResearchInstitute1, cls: 'size-[36px]' },
      { src: img1, cls: 'h-[33px] w-[38px]' },
    ],
  },
]

// 아래 버튼 두 개("Trade Market 바로가기" · "품질인증 바로가기")를 보일 것인가.
// 2026-09-26 에 우선 감춰 달라는 요청으로 껐다. 신뢰 섹션과 같은 방식이다.
const SHOW_CTA = false

const CTA_DARK = {
  backgroundImage:
    'linear-gradient(109.987385085576deg, rgb(6, 5, 5) 3.4363%, rgb(208, 155, 0) 248.16%)',
}
</script>

<template>
  <section
    ref="root"
    class="relative flex w-full flex-col justify-center"
    :style="{ '--r': rhythm }"
    :class="{ 'bg-[#fdfbf8]': !bare }"
    data-node-id="4133:20687"
  >
    <div
      class="mx-auto grid w-full max-w-[1530px] grid-cols-1 gap-x-[100px] gap-y-[60px] px-[20px] lg:grid-cols-[minmax(0,546fr)_minmax(0,859fr)]"
    >
      <!-- 왼쪽 이미지. 칸 높이를 그대로 채운다 — 격자 한 줄이라 오른쪽 글과 같아진다. -->
      <div class="relative">
        <div class="h-full overflow-hidden rounded-[40px]">
          <!-- 등장 연출(아래에서 올라오며 드러나는 역커튼)은 걸지 않는다.
               들어올 때부터 그 자리에 있어야 한다. 오른쪽 글의 연출은 그대로다.
               lg 아래에서는 한 칸으로 접혀 줄 높이를 정할 것이 없다. 그때만
               원본 비율(820:988)을 쓴다 — h-full 만 두면 0 으로 찌부러진다. -->
          <img
            :src="imgRectangle240649917"
            alt=""
            class="block aspect-[820/988] w-full object-cover lg:aspect-auto lg:h-full"
          />
        </div>
      </div>

      <!-- 오른쪽 글 -->
      <div class="flex flex-col items-start">
        <span
          class="inline-flex items-center rounded-full border border-[#a66e08] px-[20px] py-[10px] text-[15px] font-semibold leading-[22px] text-[#a66e08]"
          :style="curtain(0)"
        >
          Real Gold Market
        </span>

        <!-- 한 줄이다(2026-09-26 요청). 원본은 <br> 로 두 줄이었다.
             글 칸 859 에 한 줄이 758 이라 101 이 남는다. 칸이 그보다 좁아지면
             word-break: keep-all 로 낱말 단위로 알아서 접힌다. -->
        <!-- 2026-10-01 클라이언트 피드백으로 한 줄에서 세 줄로 바꿨다.
             줄바꿈은 <br> 로 못 박는다 - 글자 수에 맡기면 접히는 자리가 멋대로 정해진다.
             셋째 줄 원문은 "국내 최저 2.15%의 수수료만으로 가능한 금, 은거래" 였는데
             글 칸(850)에 한 줄로 안 들어가 네 줄이 됐다(56px 에서 1123). 줄여서 833 이다. -->
        <h2 class="trading-title mt-[30px]" :style="curtain(0)">
          국내 최고 품질의 안전한 금,은 거래<br />
          살 때와 팔때의 큰 가격차이가 아닌<br />
          <span class="trading-accent">국내 최저 2.15% 수수료</span>의 금, 은 거래
        </h2>

        <div class="mt-[30px] w-full" :style="curtain(1)">
          <p class="text-[22px] font-semibold leading-[30px] text-black">
            금의 가치를 넘어, 새로운 자산 생태계로
          </p>
          <div class="mt-[10px] text-[20px] leading-[28px] text-black opacity-50">
            <p v-for="(para, i) in BODY" :key="i" :class="i ? 'mt-[18px]' : ''">{{ para }}</p>
          </div>
        </div>

        <div v-if="SHOW_CTA" class="mt-[40px] flex flex-wrap gap-[10px]" :style="curtain(1)">
          <span class="trading-cta" :style="CTA_DARK">
            Trade Market 바로가기
            <span class="trading-arrow"><img :src="imgArrowUpRight" alt="" /></span>
          </span>
          <span class="trading-cta bg-gradient-to-r from-[#bf9c51] to-[#ba841e] to-[127.25%]">
            품질인증 바로가기
            <span class="trading-arrow"><img :src="imgArrowUpRight" alt="" /></span>
          </span>
        </div>

        <div class="mt-[30px] h-px w-full bg-[#d9d9d9]" :style="curtain(1)" />

        <div class="mt-[30px] flex flex-wrap gap-[30px]" :style="curtain(1)">
          <div v-for="cert in CERTS" :key="cert.label">
            <p class="text-[18px] font-medium leading-[26px] text-black">{{ cert.label }}</p>
            <div class="mt-[15px] flex gap-[10px]">
              <span v-for="(badge, i) in cert.badges" :key="i" class="trading-badge">
                <img :src="badge.src" alt="" class="object-contain" :class="badge.cls" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* 제목만 창 폭을 탄다. 1280 을 넘으면 원본 56px 에 걸려 더 커지지 않는다.
   (56 / 1280 * 100 = 4.375vw) */
.trading-title {
  /* 여섯 섹션 제목을 50 으로 맞췄다(2026-09-26 요청). 원본은 56 이었다.
     어느 해상도에서도 같은 크기라야 해서, 섹션이 통째로 줄어들 때(--fit)
     제목만 그만큼 되돌려 상쇄한다.

     거래소개만 2026-10-01 에 올렸다("다른 제목들보다 살짝만 크게 해서 차이가 날
     정도로만"). 통일이 깨지는 건 의도한 것이다. 굵기도 700 -> 800.
     56(원본 값) -> 52 -> 54 로 두 번 손봤다. 다른 제목(50)보다 4 크다.

     실측 - 글 칸은 850 이다(h2 는 items-start 라 글자만큼만 잡혀 677 로 보인다).
     54px 에서 세 줄이 731 / 716 / 803 이라 다 한 줄로 떨어진다(56 에서는 833).
     섹션 내용은 651 -> 783 이 되는데, 노트북(1366x698 · 1440x731)에서 쓸 수 있는
     자리가 841~847 이라 useFitHeight 가 줄이지 않는다. */
  font-size: calc(54px / var(--fit, 1));
  line-height: 1.18;
  font-weight: 800;
  color: #000;
  word-break: keep-all;
}

/* 강조는 셋째 줄의 "국내 최저 2.15% 수수료" 까지다(2026-10-01).
   처음엔 숫자만 넣었다가 구절 전체로 넓혔다 - 뒤의 "의 금, 은 거래" 는 검정으로
   남아 첫 줄과 짝이 맞는다. 세 줄 다 색을 넣으면 제목이 아니라 배너가 된다.
   #0058B0 은 네이비(#00264D)와 파랑(#0074E8)의 밝기 한가운데다(2026-10-01 선택).
   네이비는 옆의 검정 본문과 1.4:1 이라 색을 입혀도 강조가 안 서고, 브랜드 금색은
   이 섹션 바탕(#fdfbf8)에서 2.9:1 이라 큰 글자 기준(3:1)에도 못 미친다.
   이 값은 바탕과 6.7:1, 검정과 3.0:1 이다. */
.trading-accent {
  color: #0058b0;
}

.trading-cta {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  border-radius: 5px;
  padding: 10px 12px 10px 14px;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.16px;
  color: #fff;
  white-space: nowrap;
}

.trading-arrow {
  display: block;
  width: 16px;
  height: 16px;
  /* 원본은 16px 상자 안에서 화살표를 90도 돌려 쓴다. */
  transform: rotate(90deg);
}
.trading-arrow img {
  display: block;
  width: 100%;
  height: 100%;
}

.trading-badge {
  display: flex;
  width: 60px;
  height: 60px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(15px);
}
</style>
